import React, { useState } from 'react';
import { Modal, Tag, Tooltip } from 'antd';
import { EyeOutlined, FileAddOutlined } from '@ant-design/icons';
import moment from 'moment';
import { STUDY_DATETIME_FORMAT } from '../../../../utils/helper';
import type { Report } from '../../types';
import { getStatusStyle } from './statusStyles';
import { downloadDocumentPdf } from '../../../OnlyOfficeDocuments/documentsApi';
import PdfViewer from '../../../../components/PdfViewer';
import { Spin, message } from 'antd';

interface PriorReportsProps {
  reports: Report[];
  isSignedOff: boolean;
  onLoadAsTemplate: (report: Report) => void;
}

const PriorReports: React.FC<PriorReportsProps> = ({ reports, isSignedOff, onLoadAsTemplate }) => {
  const [viewingReport, setViewingReport] = useState<Report | null>(null);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // A doc-backed report is ALWAYS previewed by converting its .docx — that is the only
  // rendering that preserves formatting. `pr_report_html` must never win here: on an
  // OnlyOffice report it holds mammoth-extracted RAW TEXT written at sign-off for search
  // indexing (see submit-report-oodocs), not html, so rendering it drops the letterhead,
  // signature, tables and every line break. It is a fallback for legacy pre-OnlyOffice
  // reports only, which carry no document. Mirrors renderSignedReportPdf on the backend.
  const handleViewReport = async (report: any) => {
    setViewingReport(report);
    if (report.document?.doc_id) {
      setPdfLoading(true);
      try {
        const pdfBlob = await downloadDocumentPdf(report.document.doc_id);
        const buffer = await pdfBlob.arrayBuffer();
        setPdfData(buffer);
      } catch (err) {
        console.error('Error fetching PDF preview:', err);
        message.error('Failed to load document preview');
      } finally {
        setPdfLoading(false);
      }
    }
  };

  const handleCloseView = () => {
    setViewingReport(null);
    setPdfData(null);
  };

  // Ensure only the latest report per prior order is shown, irrespective of status
  const latestReports = React.useMemo(() => {
    const seen = new Set();
    const result: Report[] = [];
    for (const r of reports) {
      const orderId =(r as any)?.pr_pacs_ord_id || (r as any)?.pacs_ord_id;
      if (orderId) {
        if (!seen.has(orderId)) {
          seen.add(orderId);
          result.push(r);
        }
      } else {
        result.push(r);
      }
    }
    // Newest prior first: by study (order) time, then report creation time.
    const sortKey = (r: any) =>
      moment(r?.order?.po_req_time || r?.createdAt || 0).valueOf() || 0;
    return result.sort((a, b) => sortKey(b) - sortKey(a));
  }, [reports]);

  // Study time: order request time, falling back to the report's creation time.
  const studyMoment = (record: any) => {
    const raw = record?.order?.po_req_time || record?.createdAt;
    return raw ? moment(raw) : null;
  };

  if (!latestReports.length) {
    return <div className="no-content-message">No prior reports available</div>;
  }

  // A table frame (header row + ruled cells) around the two-line chip content. The panel
  // is too narrow for one-column-per-field, so each cell stacks a primary value over a
  // secondary one: nothing is hidden behind a horizontal scroll and nothing shrinks.
  return (
    <>
      <div className="prior-grid" role="table" aria-label="Prior reports">
        <div className="prior-grid-row prior-grid-head" role="row">
          <div className="prior-grid-cell" role="columnheader">Study</div>
          <div className="prior-grid-cell" role="columnheader">Report</div>
          <div className="prior-grid-cell" role="columnheader" aria-label="Actions" />
        </div>
        {latestReports.map((record: any) => {
          const m = studyMoment(record);
          const modality = record?.order?.po_modality;
          // Blank body parts are common on HL7-first orders — don't render a bare "—".
          const bodyPart = record?.order?.po_body_part;
          const status = record.pr_status?.replaceAll('_', ' ') || '—';
          const reportedBy = record.pr_reported_by || '—';
          return (
            <div key={record.pr_id} className="prior-grid-row" role="row">
              <div className="prior-grid-cell" role="cell">
                <div
                  className="prior-grid-primary"
                  title={[modality, bodyPart].filter(Boolean).join(' · ') || undefined}
                >
                  {modality && <span className="prior-grid-modality">{modality}</span>}
                  <span className="prior-grid-bodypart">{bodyPart || (modality ? '' : '—')}</span>
                </div>
                <div
                  className="prior-grid-secondary prior-grid-date"
                  title={m ? m.format(STUDY_DATETIME_FORMAT) : 'No study date'}
                >
                  {m ? (
                    <>
                      {m.format('DD-MM-YYYY')} <span className="prior-grid-time">{m.format('HH:mm')}</span>
                    </>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div className="prior-grid-cell" role="cell">
                <div className="prior-grid-primary">
                  <Tag className="prior-grid-status" style={getStatusStyle(record.pr_status)} title={status}>
                    {status}
                  </Tag>
                </div>
                <div className="prior-grid-secondary" title={reportedBy}>
                  {reportedBy}
                </div>
              </div>
              <div className="prior-grid-cell prior-grid-actions" role="cell">
                {/* Bare icons, not antd Buttons — a size="small" Button forces a 24px row */}
                <Tooltip title="View Report">
                  <EyeOutlined onClick={() => handleViewReport(record)} className="prior-grid-icon" />
                </Tooltip>
                <Tooltip title="Load as Template">
                  <FileAddOutlined onClick={() => onLoadAsTemplate(record)} className="prior-grid-icon" />
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
      <Modal
        title="View Prior Report"
        open={!!viewingReport}
        onCancel={handleCloseView}
        footer={null}
        width={1000}
      >
        <Spin spinning={pdfLoading} tip="Loading report preview...">
          {pdfData ? (
            <div style={{ height: '80vh', overflowY: 'auto' }}>
              <PdfViewer
                pdfArrayBuffer={pdfData}
                patientData={{ patient: viewingReport?.order?.patient }}
                mimeType="application/pdf"
                hideCopyButton
                hideWhatsappButton
                hideFullscreenButton
              />
            </div>
          ) : /* Legacy pre-OnlyOffice report (no document) — real html, safe to render. */
          !viewingReport?.document?.doc_id && viewingReport?.pr_report_html ? (
            <div
              className="report-preview-content"
              dangerouslySetInnerHTML={{ __html: viewingReport.pr_report_html }}
              style={{ padding: '20px', background: '#fff', border: '1px solid #d9d9d9', minHeight: '400px', maxHeight: '70vh', overflowY: 'auto' }}
            />
          ) : (
            !pdfLoading && <div className="no-content-message">No report content available</div>
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default PriorReports;
